import { ArrowForwardIos, FiberManualRecord } from '@mui/icons-material';
import { IconButton, Typography, useTheme } from '@mui/material';
import { useFeature } from '@next-box/feature-flags';
import { ScreenType } from '@tmdb-graphql-api/schema/types';
import { startCase } from 'lodash-es';
import { Component as CertificationIcon } from '#components/CertificationIcon/index.ts';
import { ConditionalTextLink } from '#components/ConditionalTextLink/index.tsx';
import { type TvDetailsProps } from '#components/Details/Tv/index.tsx';
import {
  CertificationIconContainer,
  Container,
  GenresContainer,
  RatingsContainer,
  SectionA,
} from '#components/Details/styled.ts';
import { type CommonDetailsHeaderProps } from '#components/Details/types.ts';
import { FavoriteBadge } from '#components/FavoriteBadge/index.tsx';
import { Link } from '#components/Link/index.tsx';
import { Optional } from '#components/Optional/index.tsx';
import { PopularityBadge } from '#components/PopularityBadge/index.tsx';
import { Query as ReviewsBadge } from '#components/ReviewsBadge/index.ts';
import { Large as VoteAverageBadge } from '#components/VoteAverageBadge/index.tsx';
import { WatchlistBadge } from '#components/WatchlistBadge/index.tsx';
import { FeatureFlags } from '#enums/featureFlags.ts';
import { formatAirDates } from '#helpers/formatAirDates.ts';
import { formatGenres } from '#helpers/formatGenres.ts';
import { formatNumberOfSeasons } from '#helpers/formatNumberOfSeasons.ts';
import { getTvCertFromContentRatings } from '#helpers/getTvCertFromContentRatings.ts';
import { useToolbelt } from '#hooks/useToolbelt.ts';

export type TvDetailsHeaderProps = TvDetailsProps & CommonDetailsHeaderProps;

export const TvDetailsHeader = ({
  compact = false,
  data,
  linkTitle = false,
  showExpandIcon = false,
  sx,
  titleLinkOnClick,
}: TvDetailsHeaderProps) => {
  const {
    contentRatings,
    firstAirDate,
    genres,
    id,
    lastAirDate,
    name,
    numberOfSeasons,
    popularity,
    status,
    tagline,
    voteAverage,
    voteCount,
  } = data;

  const theme = useTheme();
  const { config } = useToolbelt();
  const watchlistEnabled = useFeature(FeatureFlags.Watchlist);
  const sectionTypeKey = 'tv';
  const certification = getTvCertFromContentRatings(contentRatings ?? [], config.country);
  const formattedGenres = formatGenres(genres);

  return (
    <Container sx={sx}>
      <Typography
        sx={{
          fontSize: compact ? '1.8rem' : undefined,
          paddingRight: showExpandIcon ? '52px' : undefined,
          position: 'relative',
        }}
        variant="h4"
      >
        <ConditionalTextLink
          link={linkTitle}
          onClick={titleLinkOnClick}
          to={`/details/${sectionTypeKey}/${String(id)}`}
        >
          <strong>{name}</strong>
        </ConditionalTextLink>
        {showExpandIcon ? (
          <IconButton
            aria-label="Open preview in full"
            component={Link}
            sx={{
              color: theme.palette.grey[100],
              position: 'absolute',
              right: 0,
              top: 0,
            }}
            to={`/details/${sectionTypeKey}/${String(id)}`}
          >
            <ArrowForwardIos />
          </IconButton>
        ) : undefined}
      </Typography>
      <Optional>
        <Typography
          sx={{
            fontSize: compact ? '1rem' : undefined,
          }}
          variant="h6"
        >
          {tagline}
        </Typography>
      </Optional>
      <div>
        <Typography sx={{ color: theme.supportingTextPrimary }} variant="body2">
          <span>
            {firstAirDate ? formatAirDates(firstAirDate, lastAirDate, status) : startCase(status.toLowerCase())}
          </span>
          {!compact && formattedGenres ? (
            <GenresContainer>
              <FiberManualRecord
                sx={{ height: '10px', marginLeft: theme.spacing(1), marginRight: theme.spacing(1), width: '10px' }}
              />
              <span>{formattedGenres}</span>
            </GenresContainer>
          ) : undefined}
          {numberOfSeasons ? (
            <>
              <FiberManualRecord
                sx={{ height: '10px', marginLeft: theme.spacing(1), marginRight: theme.spacing(1), width: '10px' }}
              />
              <span>{formatNumberOfSeasons(numberOfSeasons)}</span>
            </>
          ) : undefined}
        </Typography>
      </div>
      <SectionA sx={{ display: 'flex', marginBottom: theme.spacing(1.5), marginTop: theme.spacing(1.5) }}>
        {certification ? (
          <CertificationIconContainer>
            <CertificationIcon
              certification={certification}
              height={compact ? 35 : 50}
              id={id}
              sx={{
                [theme.breakpoints.down('sm')]: {
                  height: 35,
                  width: 35,
                },
              }}
              type={ScreenType.Tv}
              width={compact ? 35 : 50}
            />
          </CertificationIconContainer>
        ) : undefined}
        <RatingsContainer sx={{ alignItems: compact ? 'center' : undefined }}>
          <ReviewsBadge compact={compact} id={id} title={name} type={ScreenType.Tv} />
          {watchlistEnabled ? (
            <WatchlistBadge
              compact={compact}
              id={id}
              status={status}
              sx={{ marginLeft: theme.spacing(2) }}
              type={ScreenType.Tv}
            />
          ) : undefined}
          <FavoriteBadge compact={compact} id={id} sx={{ marginLeft: theme.spacing(2) }} type={ScreenType.Tv} />
          <VoteAverageBadge
            compact={compact}
            sx={{
              [theme.breakpoints.up('md')]: {
                marginLeft: compact ? theme.spacing(1) : theme.spacing(2),
              },
              marginLeft: compact ? theme.spacing(0.5) : theme.spacing(1),
            }}
            voteAverage={voteAverage}
            voteCount={voteCount}
          />
          {compact ? undefined : (
            <PopularityBadge
              popularity={popularity}
              sx={{
                [theme.breakpoints.up('md')]: {
                  marginLeft: theme.spacing(2),
                },
                [theme.breakpoints.down('sm')]: {
                  display: 'none',
                },
                marginLeft: theme.spacing(1),
              }}
            />
          )}
        </RatingsContainer>
      </SectionA>
    </Container>
  );
};
